import React, { FC } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Typography, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CallMadeIcon from '@mui/icons-material/CallMade';
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined';
import VideoPoster from './VideoPoster';
import type { MediaItem } from '../types/common';

export interface CrosspostOriginal {
  id: string;
  title: string;
  herd?: { herdName?: string };
  author?: { username?: string };
  media?: MediaItem[];
  commentCount?: number;
  trueReactionCount?: number;
  falseReactionCount?: number;
}

interface CrosspostEmbedProps {
  original?: CrosspostOriginal | null;
  /** Snapshot used when the original was deleted (populate returns null). */
  fallbackMeta?: { authorName?: string; herdName?: string };
}

/**
 * Quiet, nested preview of a crossposted post's ORIGINAL. Reads as native to the
 * feed but muted — a slim attribution line plus the original's title/thumbnail,
 * the whole card linking to the original permalink. Attribution survives a
 * deleted original via the denormalized snapshot.
 */
const CrosspostEmbed: FC<CrosspostEmbedProps> = ({ original, fallbackMeta }) => {
  const theme = useTheme();

  const herdName = original?.herd?.herdName ?? fallbackMeta?.herdName;
  const authorName = original?.author?.username ?? fallbackMeta?.authorName;
  const removed = !original;
  const thumb = original?.media?.[0];
  const navigate = useNavigate();
  // Inner links must not bubble to the card's navigation (and avoid nested <a>).
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const attribution = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: removed ? 0 : 1, color: theme.tokens.colors.text3, minWidth: 0 }}>
      <CallMadeIcon sx={{ fontSize: 14 }} />
      <Typography component="span" sx={{ fontSize: theme.tokens.typography.sizes.postMeta, color: theme.tokens.colors.text3, whiteSpace: 'nowrap' }}>
        Shared from{' '}
        {herdName ? (
          <Link component={RouterLink} to={`/herd/${herdName}`} onClick={stop} sx={{ color: theme.tokens.colors.text2 }}>
            {herdName}
          </Link>
        ) : (
          <Box component="span" sx={{ color: theme.tokens.colors.text2 }}>a herd</Box>
        )}
        {authorName && (
          <>
            {' · '}
            <Link component={RouterLink} to={`/u/${authorName}`} onClick={stop} sx={{ color: theme.tokens.colors.text2 }}>
              @{authorName}
            </Link>
          </>
        )}
      </Typography>
    </Box>
  );

  const shell = {
    display: 'block',
    textDecoration: 'none',
    color: 'inherit',
    border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.cardBorder}`,
    borderRadius: 'var(--sh-lg)',
    backgroundColor: theme.palette.background.default,
    p: 1.25,
    cursor: 'pointer',
    transition: `border-color ${theme.tokens.animations.fast} ${theme.tokens.animations.easing}`,
    '&:hover': { borderColor: theme.tokens.colors.glassLine2 },
  } as const;

  if (removed) {
    return (
      <Box sx={{ ...shell, cursor: 'default', '&:hover': undefined }}>
        {attribution}
        <Typography sx={{ fontSize: theme.tokens.typography.sizes.postMeta, color: theme.tokens.colors.text3, fontStyle: 'italic' }}>
          The original post is no longer available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/comments/${original.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') navigate(`/comments/${original.id}`);
      }}
      sx={shell}
    >
      {attribution}
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        {thumb && (
          <Box
            sx={{
              width: 56,
              height: 56,
              flexShrink: 0,
              borderRadius: 'var(--sh-md)',
              overflow: 'hidden',
              border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.glassLine}`,
            }}
          >
            {thumb.type === 'Video' ? (
              <VideoPoster
                url={thumb.url}
                duration={thumb.duration}
                badge={false}
                // Renders in a 56px tile.
                width={180}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <img src={thumb.url} alt={original.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            )}
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: theme.tokens.typography.sansFontFamily,
              fontSize: theme.tokens.typography.sizes.postTitle,
              fontWeight: theme.tokens.typography.fontWeightMedium,
              color: theme.tokens.colors.textHeading,
              lineHeight: 1.35,
            }}
          >
            {original.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75, color: theme.tokens.colors.text3 }}>
            <Typography component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, fontFamily: theme.tokens.typography.monoFontFamily, fontSize: '11px' }}>
              <ModeCommentOutlinedIcon sx={{ fontSize: 13 }} /> {original.commentCount ?? 0}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CrosspostEmbed;
